import { JSX, useContext, useEffect, useMemo } from "react";
import { ColumnConfig, SortDirections, wafflesKeyColumnName } from "@app/shared/waffles/exports";
import { Waffles } from "@app/shared/waffles/Waffles";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import { Accordion } from "@app/shared/Accordion";
import moment from "moment";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import PageSection from "@app/shared/PageSection";
import { CreateExceptionsContext } from "@app/exceptions/contexts/CreateExceptionsContext";
import { WizardStepProps } from "@app/exceptions/contexts/interfaces";
import { SpecialtyGroupExceptionReviewStatus, SpecialtyGroupExceptionReviewStatusLabels } from "@app/exceptions/enums";
import { useQuery } from "@tanstack/react-query";
import ExceptionsService from "@app/exceptions/exceptionsService";

interface ProjectsTableData {
  [wafflesKeyColumnName]: string;
  projectName: string;
  templateName: string;
  existingExceptions: string;
}

const CreateExceptionsReview = (props: WizardStepProps): JSX.Element => {
  const wizardContext = useContext(CreateExceptionsContext);
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;
  const exceptionsService = useMemo(() => new ExceptionsService(), []);
  const selectedCustomProjectIds = wizardContext.projects.filter(project => project.isChecked)
    .map(project => project.customProjectId);

  const { data: isCCIIO } = useQuery({
    enabled: featureFlags.has(Features.CCIIO_JUSTIFICATION_EXPORT),
    queryFn: ({ signal }) => exceptionsService.getCCIIOExceptionStatus(selectedCustomProjectIds, signal),
    queryKey: [ExceptionsService.getCCIIOExceptionStatusQueryKey, selectedCustomProjectIds],
    staleTime: 5 * 60 * 1000,
  })

  /* oxlint-disable sort-keys */
  const projectsColumnConfig: ColumnConfig = {
    projectName: {
      displayText: "Projects",
      testId: "projects",
    },
    templateName: {
      displayText: "Templates",
      testId: "template",
    },
    existingExceptions: {
      displayText: "Existing Exceptions",
      testId: "existing-exceptions"
    }
  };
  /* oxlint-enable sort-keys */

  useEffect(() => {
    props.pageIsValid(wizardContext.projects.some((project) => project.isChecked));
  }, [wizardContext.projects]);

  const selectedProjectIds: string[] = useMemo(() => {
    return wizardContext.projects.filter((project) => project.isChecked).map((p) => p.customProjectId.toString());
  }, [wizardContext.projects]);

  const projectsTableData: ProjectsTableData[] = useMemo(() => {
    return wizardContext.projects
      .filter(((project) => project.isVisible && project.isChecked))
      .map<ProjectsTableData>((project) => {
        /* oxlint-disable sort-keys */
        return ({
          [wafflesKeyColumnName]: project.customProjectId.toString(),
          projectName: project.projectName,
          templateName: project.templateName,
          existingExceptions: project.hasException ? 'Yes' : 'No',
        });
        /* oxlint-enable */
      });
  }, [wizardContext.projects]);

  const customSort = (rows: ProjectsTableData[], key: keyof ProjectsTableData, direction: SortDirections) => {
    const directionMultiplier = direction === SortDirections.Asc ? 1 : -1;
    switch (key) {
      case "existingExceptions":
      case "projectName":
      case "templateName":
      default:
        return rows.sort((a, b) => (a[key] > b[key] ? 1 : -1) * directionMultiplier);
    }
  }

  const formatProjects = (): string => {
    return `${selectedProjectIds.length}/${wizardContext.projects.length} projects selected`;
  }
  const formatServiceArea = (): string => {
    const [state, county] = wizardContext.selectedServiceArea.split(":");
    return `${county} County, ${state}`;
  }

  const formatStatus = (): string => {
    return wizardContext.status.charAt(0).toUpperCase() + wizardContext.status.slice(1);
  }

  const formatReviewStatus = (): string => {
    return wizardContext.reviewStatus === SpecialtyGroupExceptionReviewStatus.None ? "---" : SpecialtyGroupExceptionReviewStatusLabels[wizardContext.reviewStatus];
  }

  const formatSpecialty = (): string => {
    const [specialty] = wizardContext.selectedSpecialty.split(":");
    return specialty;
  }

  const formatDate = (date: string): string => {
    return date ? moment(date).format("MM/DD/YYYY") : "---";
  }

  const formatPreviouslyApproved = (): string => {
    return wizardContext.previouslyApproved ? "Yes" : "No";
  }

  const formatExcludeFromCCIIOJustification = (): string => {
    return wizardContext.excludeFromCCIIOJustification ? "Yes" : "No";
  }

  return (
    <PageSection>
      <div className="container mt-3">
        <Accordion title="Review" defaultOpen={true} testId="create-exceptions__review-accordion">
          <p className="demibold mb-0">Projects Selected: <span className="fw-lighter">{formatProjects()}</span></p>
          <p className="demibold mb-0">Specialty: <span className="fw-lighter">{formatSpecialty()}</span></p>
          <p className="demibold mb-0">Service Area: <span className="fw-lighter">{formatServiceArea()}</span></p>
          <p className="demibold mb-0">Internal Review Status: <span className="fw-lighter">{formatReviewStatus()}</span></p>
          <p className="demibold mb-0">Exception Status: <span className="fw-lighter">{formatStatus()}</span></p>
          <p className="demibold mb-0">Start Date: <span className="fw-lighter">{formatDate(wizardContext.startDate)}</span></p>
          <p className="demibold mb-0">Expiration Date: <span className="fw-lighter">{formatDate(wizardContext.endDate)}</span></p>
          <p className="demibold mb-0">Previously Approved: <span className="fw-lighter">{formatPreviouslyApproved()}</span></p>
          {featureFlags.has(Features.CCIIO_JUSTIFICATION_EXPORT) && isCCIIO && (
            <p className="demibold mb-0">Exclude From CCIIO Justification Extract: <span className="fw-lighter">{formatExcludeFromCCIIOJustification()}</span></p>
          )}
          <p className="demibold mb-0">Comments: <span className="fw-lighter">{wizardContext.comment}</span></p>
          <p className="demibold mb-0">Exception Narrative: <span className="fw-lighter">{wizardContext.narrative}</span></p>
          <p className="demibold mb-0">Attachment(s):
            <span className="fw-lighter">
              {wizardContext.attachments.map((attachment, index) => (
                <span key={`${attachment.documentName}_${index}`} className="d-block">{attachment.documentName}</span>
              ))}
            </span>
          </p>
        </Accordion>
      </div>

      <div className="container mt-4">
        <AccordionContainer className="mt-3">
          <Accordion title="Selected Projects" defaultOpen={true} testId="create-exceptions__selected-projects-accordion">
            <Waffles<ProjectsTableData>
              columns={projectsColumnConfig}
              customSort={customSort}
              data={projectsTableData}
              disableProcessingOverlay={true}
              fullWidth={true}
              selectedKeys={selectedProjectIds}
              showCheckboxes={false}
              testId="create-exceptions-projects-table"
            />
          </Accordion>
        </AccordionContainer>
      </div>
    </PageSection>
  );
};

export default CreateExceptionsReview;
